import { useState } from "react";
import { api, SIGNUP_URL, DEMO_URL } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpRight, Wallet, Apple } from "lucide-react";
import WalletCard from "./WalletCard";

const HERO_BG =
  "https://images.unsplash.com/photo-1672767775752-b457456f8980?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxmb29kJTIwdHJ1Y2slMjBuZW9uJTIwbmlnaHR8ZW58MHx8fHwxNzgzOTk3MDc0fDA&ixlib=rb-4.1.0&w=1920&q=55";

export default function Hero() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) {
      toast.error("Enter your email to get started");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      toast.error("Enter a valid email address");
      return;
    }
    try {
      setLoading(true);
      await api.post("/leads", { email: value, source: "hero" });
      toast.success("You're in. Check your inbox soon.", {
        description: "We'll be in touch with your 14-day trial link.",
      });
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast.error("Couldn't sign you up", { description: String(detail) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] pt-24 pb-16 lg:pt-32 lg:pb-24 overflow-hidden"
    >
      {/* Background layers */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      {/* Base navy tint so the image reads as brand */}
      <div className="absolute inset-0 bg-[#07182D]/55 z-0" />
      {/* Strong left-side gradient for text readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#07182D] via-[#07182D]/85 to-[#07182D]/20 z-0" />
      {/* Bottom-up subtle fade */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#07182D] z-0" />
      <div className="absolute inset-0 grid-lines z-0 opacity-40" />
      <div className="absolute inset-0 grain z-0" />

      {/* Warm glow */}
      <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#D4A017]/14 blur-[120px] z-0" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-[#D4A017]/10 blur-[140px] z-0" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-6 items-center">
          {/* LEFT — headline */}
          <div className="lg:col-span-7">
            <div
              className="inline-flex items-center gap-2 border border-white/20 bg-white/5 backdrop-blur-md px-3 py-1.5 mb-6"
              data-testid="hero-eyebrow"
            >
              <span className="w-1.5 h-1.5 bg-[#D4A017] rounded-full animate-pulse" />
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/80">
                Built for food trucks · Est. 2024
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display uppercase text-white leading-[0.86] tracking-tight text-[56px] sm:text-[80px] lg:text-[104px] xl:text-[120px]"
            >
              Keep your line<br />
              <span className="text-[#D4A017]">coming back.</span>
            </h1>

            <p
              data-testid="hero-subhead"
              className="mt-6 max-w-xl text-white/85 text-base sm:text-lg leading-relaxed"
            >
              The simplest digital loyalty program for food trucks & street vendors.
              Punch cards, cashback, gift cards & push notifications — living in
              Apple Wallet & Google Pay. No app to install. No hardware to buy.
            </p>

            <div className="mt-8 flex items-center gap-4 text-white/60 font-mono text-[11px] uppercase tracking-widest">
              <div className="flex items-center gap-2">
                <Apple size={14} /> Apple Wallet
              </div>
              <span className="w-1 h-1 bg-white/30 rounded-full" />
              <div className="flex items-center gap-2">
                <Wallet size={14} /> Google Pay
              </div>
              <span className="w-1 h-1 bg-white/30 rounded-full" />
              <div>14-day free trial</div>
            </div>

            <form
              onSubmit={submit}
              noValidate
              data-testid="hero-form"
              className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl"
            >
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@foodtruck.com"
                data-testid="hero-email-input"
                className="flex-1 bg-[#0B1F3A]/70 border-2 border-white/20 focus:border-[#D4A017] outline-none px-4 py-3.5 text-white placeholder:text-white/40 font-mono text-sm transition-colors"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="hero-submit"
                className="btn-brutal bg-[#D4A017] text-[#07182D] font-display uppercase tracking-wide px-6 py-3.5 border-2 border-[#D4A017] shadow-hard-sm flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {loading ? "Sending…" : "Get early access"}
                <ArrowUpRight size={18} strokeWidth={2.5} />
              </button>
            </form>

            <div className="mt-6 flex flex-wrap items-center gap-4">
              <a
                href={SIGNUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-start-trial"
                className="font-mono text-xs uppercase tracking-widest text-white underline underline-offset-4 decoration-[#D4A017] decoration-2 hover:text-[#D4A017] transition-colors"
              >
                → Start free trial (no card)
              </a>
              <a
                href={DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-book-demo"
                className="font-mono text-xs uppercase tracking-widest text-white/60 hover:text-white transition-colors"
              >
                Book a demo
              </a>
            </div>
          </div>

          {/* RIGHT — floating wallet cards */}
          <div className="lg:col-span-5 relative min-h-[520px] hidden lg:block">
            <div className="absolute top-0 right-8 w-[300px] animate-float" data-testid="hero-card-1">
              <WalletCard
                title="TACO LIBRE"
                subtitle="Buy 10, get 1 free"
                bg="linear-gradient(135deg,#0B1F3A 0%,#07182D 100%)"
                accent="#D4A017"
                logoLabel="TL"
                stamps={7}
                variant="stamps"
              />
            </div>
            <div className="absolute top-40 right-40 w-[280px] rotate-[-6deg] animate-float-delay" data-testid="hero-card-2">
              <WalletCard
                title="SMOKED & CO"
                subtitle="Cashback member"
                bg="linear-gradient(135deg,#D4A017 0%,#8a6a12 100%)"
                accent="#07182D"
                logoLabel="SC"
                points={1240}
                variant="cashback"
              />
            </div>
            <div className="absolute bottom-0 right-16 w-[290px] rotate-[4deg] animate-float" data-testid="hero-card-3">
              <WalletCard
                title="BODEGA BOWL"
                subtitle="Gift card · $50"
                bg="linear-gradient(135deg,#F8F6F2 0%,#E5E7EB 100%)"
                accent="#0B1F3A"
                logoLabel="BB"
                balance={50}
                variant="gift"
                textOnLight
              />
            </div>

            {/* Sticker */}
            <div
              data-testid="hero-sticker"
              className="absolute -left-4 bottom-24 rotate-[-8deg] bg-[#D4A017] text-[#07182D] font-display uppercase px-4 py-2 shadow-hard-white border-2 border-[#07182D]"
            >
              +2× return rate
            </div>
          </div>
        </div>

        {/* Bottom row on mobile: show one card */}
        <div className="lg:hidden mt-12 grid gap-4 max-w-sm mx-auto">
          <WalletCard
            title="TACO LIBRE"
            subtitle="Buy 10, get 1 free"
            bg="linear-gradient(135deg,#0B1F3A 0%,#07182D 100%)"
            accent="#D4A017"
            logoLabel="TL"
            stamps={7}
            variant="stamps"
          />
        </div>
      </div>
    </section>
  );
}
